"use client";

import { Check } from "lucide-react";
import { usePathname } from "next/navigation";
import { useEffect, useRef, useState } from "react";
import { ZoneLink } from "@/components/layout/zone-link";
import { buttonClass } from "@/components/ui/button";
import { useAnalytics } from "@/lib/analytics/hooks";
import { classifyCheckoutFailure } from "@/lib/billing/checkout-failure-reason";
import { startSubscriptionCheckout } from "@/lib/checkout-api";
import {
	buildOfferCopy,
	buildReminder,
	isJobDetailPath,
	type OfferProduct,
	POST_SIGNUP_OFFER_ENTRY_SURFACE,
	POST_SIGNUP_OFFER_STORAGE_KEY,
	type PostSignupOfferMoment,
	parseReminder,
} from "@/lib/experiments/post-signup-offer";
import { SIGNUP_ALERT_PARAM } from "@/lib/experiments/signup-auto-alert";
import { getPostSignupOfferDecision } from "@/server/actions/post-signup-offer";

type Props = { locale: string };

type Offer = {
	moment: PostSignupOfferMoment;
	market: string;
	product: OfferProduct;
	price: string;
};

/** Marks this tab's landing as handled so a reload never re-asks. */
const LANDING_SEEN_KEY = "ld_post_signup_offer_landing";

function readStorage(store: "local" | "session", key: string): string | null {
	try {
		return (store === "local" ? localStorage : sessionStorage).getItem(key);
	} catch {
		return null;
	}
}

function writeStorage(
	store: "local" | "session",
	key: string,
	value: string | null,
): void {
	try {
		const s = store === "local" ? localStorage : sessionStorage;
		if (value === null) s.removeItem(key);
		else s.setItem(key, value);
	} catch {
		// Storage blocked: the reminder simply never fires.
	}
}

/** Which moment (if any) this page view is. */
function detectMoment(pathname: string): PostSignupOfferMoment | null {
	let hasParam = false;
	try {
		hasParam = new URL(window.location.href).searchParams.has(
			SIGNUP_ALERT_PARAM,
		);
	} catch {
		hasParam = false;
	}
	if (hasParam && readStorage("session", LANDING_SEEN_KEY) === null) {
		return "landing";
	}
	if (
		isJobDetailPath(pathname) &&
		parseReminder(
			readStorage("local", POST_SIGNUP_OFFER_STORAGE_KEY),
			Date.now(),
		)
	) {
		return "job_detail";
	}
	return null;
}

/**
 * Post-sign-up Pro offer (`post-signup-offer` split test). Mounted in the
 * public and seeker-zone layouts; renders nothing unless this view is the
 * post-sign-up landing (`?signup_alert=`, which /signup appends to every
 * seeker callback) or the first job detail after a dismiss there. The server
 * decides eligibility (account under 24 h, not Pro) and the arm; only the
 * `offer` arm renders. Never blocks the page: one tap dismisses it.
 */
export function PostSignupOffer({ locale }: Props) {
	const pathname = usePathname();
	const { posthog } = useAnalytics();
	const busyRef = useRef(false);
	const shownRef = useRef(false);
	const [offer, setOffer] = useState<Offer | null>(null);
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState(false);

	// Runs per page view (pathname), never on a dismiss: a dismiss on a job
	// detail landing must not re-ask on that same page.
	useEffect(() => {
		if (busyRef.current || shownRef.current) return;
		const moment = detectMoment(pathname ?? "");
		if (!moment) return;
		busyRef.current = true;
		if (moment === "landing") writeStorage("session", LANDING_SEEN_KEY, "1");
		// One reminder only, consumed whether or not it renders.
		else writeStorage("local", POST_SIGNUP_OFFER_STORAGE_KEY, null);
		let cancelled = false;
		void getPostSignupOfferDecision(locale)
			.then((d) => {
				if (cancelled || !d.eligible) return;
				// Denominator for the readout: one per new account, both arms.
				if (moment === "landing") {
					posthog?.capture("post_signup_offer_eligible", {
						arm: d.arm,
						locale,
					});
				}
				if (d.arm !== "offer") return;
				shownRef.current = true;
				setOffer({
					moment,
					market: d.market,
					product: d.product,
					price: d.price,
				});
				posthog?.capture("post_signup_offer_shown", {
					arm: d.arm,
					moment,
					product_key: d.product,
					market: d.market,
					locale,
				});
			})
			.catch(() => {
				// Safe-off: no card.
			})
			.finally(() => {
				busyRef.current = false;
			});
		return () => {
			cancelled = true;
		};
	}, [pathname, locale, posthog]);

	if (!offer) return null;

	const copy = buildOfferCopy({
		locale,
		product: offer.product,
		price: offer.price,
	});
	const currency = offer.market === "global" ? "USD" : "IDR";
	const props = {
		arm: "offer",
		moment: offer.moment,
		product_key: offer.product,
		market: offer.market,
		locale,
	};

	const dismiss = () => {
		posthog?.capture("post_signup_offer_dismissed", props);
		writeStorage(
			"local",
			POST_SIGNUP_OFFER_STORAGE_KEY,
			offer.moment === "landing" ? buildReminder(Date.now()) : null,
		);
		shownRef.current = false;
		setOffer(null);
	};

	const checkout = async () => {
		if (busy) return;
		setBusy(true);
		setError(false);
		writeStorage("local", POST_SIGNUP_OFFER_STORAGE_KEY, null);
		posthog?.capture("post_signup_offer_clicked", props);
		// Click intent even when the request never reaches the server;
		// `createCheckout` stays the sole `checkout_started` emitter.
		posthog?.capture("checkout_cta_clicked", {
			currency,
			product_type: "seeker_pro",
			plan_name: offer.product,
			entry_surface: POST_SIGNUP_OFFER_ENTRY_SURFACE,
			locale,
			market: offer.market,
		});
		try {
			const result = await startSubscriptionCheckout(
				offer.product,
				offer.market,
				POST_SIGNUP_OFFER_ENTRY_SURFACE,
			);
			if ("url" in result) {
				window.location.href = result.url;
				return;
			}
			posthog?.capture("checkout_failed", {
				currency,
				product_type: "seeker_pro",
				plan_name: offer.product,
				entry_surface: POST_SIGNUP_OFFER_ENTRY_SURFACE,
				locale,
				market: offer.market,
				error_code: result.error,
				reason: classifyCheckoutFailure(result.error, result.message),
			});
		} catch {
			// Fall through to the retryable error.
		}
		setError(true);
		setBusy(false);
	};

	return (
		<div className="mx-auto max-w-4xl px-4 pt-3">
			<section
				aria-labelledby="post-signup-offer-title"
				data-testid="post-signup-offer"
				data-moment={offer.moment}
				className="rounded-xl border border-primary/30 bg-card px-4 py-4 text-foreground"
			>
				<div className="flex items-start gap-3">
					<div className="flex-1">
						<h2
							id="post-signup-offer-title"
							className="text-sm font-semibold text-foreground"
						>
							{copy.title}
						</h2>
						<ul className="mt-2 space-y-1">
							{copy.features.map((f) => (
								<li
									key={f}
									className="flex items-start gap-2 text-sm text-muted-foreground"
								>
									<Check
										aria-hidden="true"
										className="mt-0.5 size-4 shrink-0 text-primary"
									/>
									<span>{f}</span>
								</li>
							))}
						</ul>
						<p
							data-testid="post-signup-offer-price"
							className="mt-2 text-sm font-medium text-foreground"
						>
							{copy.priceLine}
						</p>
						<div className="mt-3 flex flex-wrap items-center gap-2">
							<button
								type="button"
								data-testid="post-signup-offer-cta"
								onClick={checkout}
								disabled={busy}
								className={buttonClass({ size: "sm" })}
							>
								{busy ? copy.busy : copy.cta}
							</button>
							<button
								type="button"
								data-testid="post-signup-offer-dismiss"
								onClick={dismiss}
								disabled={busy}
								className={buttonClass({ variant: "ghost", size: "sm" })}
							>
								{copy.dismiss}
							</button>
							<ZoneLink
								href={`/${locale}/pricing`}
								className="text-sm text-primary underline underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
							>
								{copy.compare}
							</ZoneLink>
						</div>
						{error ? (
							<p role="alert" className="mt-2 text-xs text-destructive">
								{copy.error}
							</p>
						) : null}
					</div>
				</div>
			</section>
		</div>
	);
}
